import { BedIcon } from '@phosphor-icons/react';
import { monthShort, parseDateOnly } from '@voll-intelligence/dates';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { useCheckoutEnv } from '../../drawer/env_context.js';
import type { CheckoutHotelCopy } from '../../model/checkout_copy.js';
import type { HotelItemSource } from '../../model/checkout_source.js';
import { hotelCancellationLabel } from './cancellation.js';
import './item_detail.css';

type HotelItemDetail = HotelItemSource['detail'];

export interface HotelItemDetailProps {
  detail: HotelItemDetail;
}

/** Dia e mês curto, separados porque a janela diz o mês UMA vez quando a
 *  estadia não vira o mês. `undefined` quando a data não é uma ISO válida. */
function datePieces(iso: string, locale: string): { day: string; month: string } | undefined {
  const d = parseDateOnly(iso);
  if (!d) return undefined;
  return { day: String(d.getDate()), month: monthShort(d, locale) };
}

/** Janela da estadia no desenho do DesignHub: `25 → 28 ago`. O mês repetido nas
 *  duas pontas é ruído — só nasce quando a estadia atravessa o mês. */
function stayWindow(detail: HotelItemDetail, locale: string): string | undefined {
  if (!detail.checkIn) return undefined;
  const from = datePieces(detail.checkIn, locale);
  if (!from) return detail.checkIn;
  const to = detail.checkOut ? datePieces(detail.checkOut, locale) : undefined;
  if (!to) return `${from.day} ${from.month}`;
  return from.month === to.month
    ? `${from.day} → ${to.day} ${to.month}`
    : `${from.day} ${from.month} → ${to.day} ${to.month}`;
}

/** A legenda sob o nome: janela · noites · endereço, na ordem do protótipo. */
export function staySummary(
  detail: HotelItemDetail,
  locale: string,
  copy: CheckoutHotelCopy,
): string {
  return [
    stayWindow(detail, locale),
    detail.nights ? copy.nights(detail.nights) : undefined,
    detail.address,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Compact variant — o cabeçalho do resumo de hotel no `CheckoutCard`: foto,
 *  nome e a legenda da estadia. O que a pessoa escolheu (quarto e plano) e o
 *  dinheiro moram no RODAPÉ do card, ao lado do gesto, como no DesignHub —
 *  aqui em cima fica só o reconhecimento do hotel. */
export function HotelItemDetailCompact({ detail }: Readonly<HotelItemDetailProps>) {
  const { copy, locale } = useCheckoutEnv();
  const summary = staySummary(detail, locale, copy.hotel);
  const thumb = useImageFallback(detail.thumbUrl);
  return (
    <div className="pcc-hotel__head">
      <span className="pcc-hotel__thumb" aria-hidden="true">
        {thumb.src ? (
          <img src={thumb.src} alt="" onError={thumb.onError} />
        ) : (
          <BedIcon size={22} aria-hidden />
        )}
      </span>
      <div className="pcc-hotel__identity">
        <p className="pcc-hotel__name">{detail.hotelName ?? copy.hotel.room}</p>
        {summary && (
          <p className="pcc-hotel__stay" aria-label={copy.hotel.stayAria}>
            {summary}
          </p>
        )}
      </div>
    </div>
  );
}

/** Expanded variant — o painel "Sua reserva" do drawer não tem rodapé onde o
 *  par quarto/plano possa morar, então ele volta pra baixo do cabeçalho. A
 *  condição de cancelamento é a última chance de vê-la antes de Solicitar e
 *  ganha linha própria: no fim da linha do quarto, que corta com reticências,
 *  ela sumia ("Café da manhã · Ree…", C3). A linha do quarto leva o texto
 *  inteiro no `title` para quem passa o mouse. */
export function HotelItemDetailExpanded({ detail }: Readonly<HotelItemDetailProps>) {
  const { copy, locale } = useCheckoutEnv();
  const cancellation = hotelCancellationLabel(detail, copy.hotel, locale);
  const roomLabel = [detail.roomName, copy.hotel.board(detail.board)].filter(Boolean).join(' · ');
  return (
    <div className="pcc-hotel">
      <HotelItemDetailCompact detail={detail} />
      {roomLabel && (
        <p className="pcc-hotel__room" title={roomLabel}>
          {roomLabel}
        </p>
      )}
      {cancellation && (
        <p
          className={`m-0 text-caption ${cancellation.charged ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {cancellation.text}
        </p>
      )}
      {detail.roomSource ? (
        <p className="text-caption text-muted-foreground">{detail.roomSource}</p>
      ) : null}
    </div>
  );
}
